import { LineChart, Line, ResponsiveContainer, ReferenceLine, YAxis, Tooltip } from "recharts";
import { ArrowDownRight, ArrowUpRight, MessageSquareText, UserRound } from "lucide-react";
import { StackedBar } from "./StackedBar";
import { STATUS, pct } from "./flashTheme";

const Delta = ({ form }) => {
  if (form.delta === null || form.delta === undefined) {
    return <span className="text-slate-400">No prior month data</span>;
  }
  const up = form.delta >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-semibold ${up ? "text-emerald-700" : "text-rose-700"}`}>
      <Icon className="w-3.5 h-3.5" />
      {form.delta > 0 ? "+" : ""}
      {form.delta.toFixed(2)} pts
      <span className="font-sans font-normal text-slate-500">vs {pct(form.prior?.on_time_pct)}</span>
    </span>
  );
};

const Spark = ({ form }) => (
  <div className="h-10 w-28 shrink-0" data-testid={`sparkline-${form.id}`}>
    {form.sparkline.length > 0 && (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={form.sparkline} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <YAxis hide domain={["dataMin - 4", "dataMax + 4"]} />
          <ReferenceLine y={form.goal_pct} stroke="#94a3b8" strokeDasharray="3 3" />
          <Tooltip
            contentStyle={{ fontSize: 11, padding: "2px 6px", borderRadius: 6 }}
            labelFormatter={(_, p) => p?.[0]?.payload?.date}
            formatter={(v) => [`${v}%`, form.primary_label]}
          />
          <Line type="monotone" dataKey="on_time_pct" stroke="#0284c7" strokeWidth={2} dot={false} isAnimationActive />
        </LineChart>
      </ResponsiveContainer>
    )}
  </div>
);

export const FlashScorecard = ({ form, index, onNotes }) => {
  const st = STATUS[form.status];
  const cur = form.current;
  return (
    <article
      className="flash-rise bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:border-slate-300 transition-[box-shadow,border-color] duration-200 flex flex-col gap-4 relative overflow-hidden"
      style={{ animationDelay: `${120 + index * 70}ms` }}
      data-testid={`form-scorecard-${form.id}`}
    >
      <span className="absolute left-0 top-0 h-full w-1" style={{ background: st?.stroke || "#e2e8f0" }} />
      <header className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-slate-900 leading-snug">{form.name}</h3>
          <p className="text-xs text-slate-500 italic mt-0.5">{form.standard || "No standard set"}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {form.owner && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 rounded-md px-2 py-0.5">
              <UserRound className="w-3 h-3" /> {form.owner}
            </span>
          )}
          <button
            onClick={() => onNotes(form)}
            className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-2 py-0.5 border transition-colors ${form.comment_count ? "text-sky-700 border-sky-200 bg-sky-50 hover:bg-sky-100" : "text-slate-500 border-slate-200 hover:bg-slate-50"}`}
            data-testid={`notes-btn-${form.id}`}
          >
            <MessageSquareText className="w-3 h-3" /> Notes{form.comment_count ? ` · ${form.comment_count}` : ""}
          </button>
        </div>
      </header>
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-heading font-semibold">
            {form.primary_label} {form.current_period ? `· ${form.current_period}` : ""}
          </div>
          <div className={`font-mono text-4xl font-bold tracking-tight ${st?.text || "text-slate-300"}`} data-testid={`on-time-${form.id}`}>
            {cur ? pct(cur.on_time_pct) : "—"}
          </div>
        </div>
        <Spark form={form} />
      </div>
      <StackedBar current={cur} goal={form.goal_pct} categories={form.categories} testId={`stacked-bar-${form.id}`} />
      <footer className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100 text-xs">
        <Delta form={form} />
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-slate-500">Goal {form.goal_pct}%</span>
          {st ? (
            <span className={`border rounded-full px-2 py-0.5 font-semibold ${st.pill}`} data-testid={`status-badge-${form.id}`}>
              {st.label}
            </span>
          ) : (
            <span className="border rounded-full px-2 py-0.5 text-slate-400 border-slate-200" data-testid={`status-badge-${form.id}`}>
              Not reported
            </span>
          )}
        </div>
      </footer>
    </article>
  );
};
